import styled, { css } from 'styled-components';
import { variableFontRegular } from 'styles/theme/text';
import { useRouter } from 'next/router';

import { FeatureFlags } from 'featureFlags/constants';
import { IConstructorFacetMultiple } from 'shared/types/ConstructorFacet';
import { SearchFilterLink } from 'search/components/searchGrid/SearchFilterLink';
import {
  SearchFilterListOption,
  SearchFilterPillStyle,
  SearchFiltersHoverAndSelectedStyle,
} from 'search/styles/SearchStyles';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { getConstructorCategoriesStrings } from 'search/enums/translations';

interface ISearchFilterDiscountAndAvailableNow {
  availableNowFacet: IConstructorFacetMultiple;
  discountTagFacet: IConstructorFacetMultiple;
  resultIds: Array<string>;
}

export const SearchFilterDiscountAndAvailableNow = ({
  availableNowFacet,
  discountTagFacet,
  resultIds,
}: ISearchFilterDiscountAndAvailableNow) => {
  const isOnSaleEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_ON_SALE);
  const eventTrackingEventContext = useEventContext();
  const { query } = useRouter();

  const underRetail = discountTagFacet?.options?.find((facet) => facet.value === 'under_retail');
  const sale =
    isOnSaleEnabled && discountTagFacet?.options?.find((facet) => facet.value === 'sale');
  const availableNow = availableNowFacet?.options?.find((facet) => facet.value === 'True');

  const handleOnClick = (filterName, optionValue) => {
    sendTrackingEvent('FILTER_APPLY_TAP', {
      event_context: eventTrackingEventContext,
      filter_name: filterName,
      filter_selection: optionValue,
      result_id: resultIds[0],
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query?.query.toString() }),
    });
  };

  return (
    <>
      {underRetail && (
        <SearchFilterLink facetType={discountTagFacet.name} option={underRetail}>
          <SearchFilterDiscountAndAvailableNow.SearchFilterListOption
            $isSelected={underRetail.status === 'selected'}
            onClick={() => handleOnClick(discountTagFacet.name, underRetail.value)}
          >
            {getConstructorCategoriesStrings(underRetail.display_name)}
          </SearchFilterDiscountAndAvailableNow.SearchFilterListOption>
        </SearchFilterLink>
      )}
      {availableNow && (
        <SearchFilterLink facetType={availableNowFacet.name} option={availableNow}>
          <SearchFilterDiscountAndAvailableNow.SearchFilterListOption
            $isSelected={availableNow.status === 'selected'}
            onClick={() => handleOnClick(availableNowFacet.name, availableNow.value)}
          >
            {getConstructorCategoriesStrings(availableNow.display_name)}
          </SearchFilterDiscountAndAvailableNow.SearchFilterListOption>
        </SearchFilterLink>
      )}
      {sale && (
        <SearchFilterLink facetType={discountTagFacet.name} option={sale}>
          <SearchFilterDiscountAndAvailableNow.SearchFilterListOption
            $isSelected={sale.status === 'selected'}
            onClick={() => handleOnClick(discountTagFacet.name, sale.value)}
          >
            {getConstructorCategoriesStrings(sale.display_name)}
          </SearchFilterDiscountAndAvailableNow.SearchFilterListOption>
        </SearchFilterLink>
      )}
    </>
  );
};

SearchFilterDiscountAndAvailableNow.SearchFilterListOption = styled(SearchFilterListOption)`
  ${SearchFilterPillStyle};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
    border: none;
    display: block;
    margin: 0;
    padding: 0 0 4px;
    text-align: right;
    width: 100%;
    ${({ $isSelected }) =>
      $isSelected &&
      css`
        ${SearchFiltersHoverAndSelectedStyle};
        color: ${({ theme }) => theme.navigation.textColor};
        background-color: ${({ theme }) => theme.navigation.backgroundColor};
      `};

    &:hover {
      ${SearchFiltersHoverAndSelectedStyle};
      color: ${({ theme }) => theme.navigation.textColor};
    }
  }
`;
